import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import { OFFLINE_STATUS_SNACKBAR_COLORS } from '../constants/offlineStatusToolbarLabels';

const getMessage = (canPunch) => {
  const action = canPunch ? 'complete your punch' : 'cancel your punch';

  return (
    <>
      We haven't noticed any activity, so we're going to{' '}
      <strong>{action}</strong>.
    </>
  );
};

const getCountdownProgress = (countdownDuration, countdownTimer) => {
  if (countdownDuration <= 0) {
    return 0;
  }

  return ((countdownDuration - countdownTimer) / countdownDuration) * 100;
};

export default function InactivityWarningSnackbar({
  canPunch,
  countdownDuration,
  countdownTimer,
  open,
}) {
  const progress = useMemo(
    () => getCountdownProgress(countdownDuration, countdownTimer),
    [countdownDuration, countdownTimer]
  );

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null}
      id="sc-clock_prompts_inactivity_snackbar"
      open={open}
    >
      <Alert
        icon={false}
        severity="warning"
        variant="outlined"
        sx={{
          alignItems: 'flex-start',
          backgroundColor: OFFLINE_STATUS_SNACKBAR_COLORS.background,
          borderColor: OFFLINE_STATUS_SNACKBAR_COLORS.main,
          color: OFFLINE_STATUS_SNACKBAR_COLORS.main,
          maxWidth: 480,
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1,
            }}
          >
            <WarningIcon aria-hidden sx={{ flexShrink: 0 }} />
            <Typography
              component="p"
              sx={{
                fontWeight: 400,
                lineHeight: 1.4,
                m: 0,
              }}
            >
              {getMessage(canPunch)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <LinearProgress
              aria-label="Inactivity countdown"
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 1,
                backgroundColor: alpha(
                  OFFLINE_STATUS_SNACKBAR_COLORS.main,
                  0.2
                ),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: OFFLINE_STATUS_SNACKBAR_COLORS.main,
                },
              }}
              value={progress}
              variant="determinate"
            />
            <Typography
              sx={{
                color: OFFLINE_STATUS_SNACKBAR_COLORS.main,
                flexShrink: 0,
              }}
              variant="caption"
            >
              {countdownTimer}s
            </Typography>
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
}

InactivityWarningSnackbar.propTypes = {
  canPunch: PropTypes.bool.isRequired,
  countdownDuration: PropTypes.number.isRequired,
  countdownTimer: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
};
